import deepFreeze from "deep-freeze";
import { UserRole } from "react-ai-platform-auth";

import { getEnv } from "./getEnv";
import {
  AssessmentCategory,
  CommentOption,
  DistributionType,
  FieldSize,
  ImageAnnotationTypes,
  InputTypes,
  OutputTypes,
  ProjectType,
  EligibilityConditions,
  AssessmentCategoryTypes,
  DateFilterTypes,
  FieldValidTypes,
  InputMethod,
} from "./types";
import { LANGUAGES } from "./components/Project/Form/languages";
import useFeatureFlag from "./hooks/useFeatureFlag";

export const DEFAULT_SEARCH_DELAY = 500;

export const DATE_OPTIONS = deepFreeze([
  { value: DateFilterTypes.ANYTIME, label: "Anytime" },
  { value: DateFilterTypes.PAST_WEEK, label: "Past week" },
  { value: DateFilterTypes.PAST_MONTH, label: "Past month" },
  { value: DateFilterTypes.PAST_YEAR, label: "Past year" },
  { value: DateFilterTypes.DATE, label: "Select a date" },
  { value: DateFilterTypes.DATE_RANGE, label: "Select a range" },
]);
export const IMAGE_ANNOTATION_OPTIONS = deepFreeze([
  { value: ImageAnnotationTypes.BOUNDING_BOX, label: "Bounding Box" },
  { value: ImageAnnotationTypes.KEYPOINT, label: "Keypoint" },
  { value: ImageAnnotationTypes.POLYGON, label: "Polygon" },
  { value: ImageAnnotationTypes.LINE, label: "Line" },
  { value: ImageAnnotationTypes.CIRCLE, label: "Circle" },
]);

export const COMMENT_OPTIONS = deepFreeze([
  { value: CommentOption.REQUIRED, label: "Require a comment" },
  { value: CommentOption.NONE, label: "Disable comments" },
  { value: CommentOption.OPTIONAL, label: "Enable optional comments" },
]);

export const DISTRIBUTION_TYPE_OPTIONS = deepFreeze([
  { value: DistributionType.PER_ITEM, label: "Per item" },
  { value: DistributionType.PER_GROUP, label: "Per group" },
]);

export const INPUT_TYPES_OPTIONS = deepFreeze([
  { value: InputTypes.TEXT, label: "Text" },
  { value: InputTypes.IMAGE, label: "Image" },
  { value: InputTypes.VIDEO, label: "Video" },
  { value: InputTypes.AUDIO, label: "Audio" },
]);

export const FIELD_SIZE_OPTIONS = deepFreeze([
  { value: FieldSize.LARGE, label: "Large" },
  { value: FieldSize.MEDIUM, label: "Medium" },
  { value: FieldSize.SMALL, label: "Small" },
]);

export const INPUT_METHOD_OPTIONS = deepFreeze([
  { value: InputMethod.CAMERA_OR_GALLERY, label: "Either Camera or Gallery" },
  { value: InputMethod.CAMERA, label: "Camera" },
  { value: InputMethod.GALLERY, label: "Gallery" },
]);

export const TEXT_SIZE_OPTIONS = deepFreeze([
  { value: FieldValidTypes.NONE, label: "None" },
  { value: FieldValidTypes.MAX_COUNT, label: "Set maximum character count" },
]);

const _outputTypesOptions = [
  { value: OutputTypes.FREE_TEXT, label: "Free text" },
  { value: OutputTypes.CATEGORIES, label: "Multiple choice answer(s)" },
  { value: OutputTypes.TEXT_ANNOTATION, label: "Text annotation" },
  { value: OutputTypes.IMAGE_ANNOTATION, label: "Image annotation" },
  { value: OutputTypes.SEMANTIC_ANNOTATION, label: "Semantic annotation" },
  { value: OutputTypes.DATE, label: "Date" },
  { value: OutputTypes.TIME, label: "Time" },
  { value: OutputTypes.NUMBER, label: "Number" },
  { value: OutputTypes.AUDIO_COLLECTION, label: "Audio recording" },
  { value: OutputTypes.VIDEO_COLLECTION, label: "Video collection" },
];

export const OUTPUT_TYPES_OPTIONS = deepFreeze(_outputTypesOptions);
export const OUTPUT_TYPES_OPTIONS_WITH_IMAGE_COLLECTION = deepFreeze([
  ..._outputTypesOptions,
  { value: OutputTypes.IMAGE_COLLECTION, label: "Image collection" },
]);

export const SINGLE_TEXT_OUTPUT_TYPES = deepFreeze([OutputTypes.TEXT_ANNOTATION]);

export const CAN_ADD_PROJECT_TYPE_EXCLUSIONS = deepFreeze([ProjectType.MOBILE_SURVEY]);

export const SINGLE_OUTPUT_TYPES = deepFreeze([
  OutputTypes.TEXT_ANNOTATION,
  OutputTypes.IMAGE_ANNOTATION,
  OutputTypes.SEMANTIC_ANNOTATION,
]);

export const IMAGE_OUTPUT_TYPES = deepFreeze([
  OutputTypes.IMAGE_ANNOTATION,
  OutputTypes.SEMANTIC_ANNOTATION,
]);

export const VIDEO_COLLECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.FREE_TEXT,
  OutputTypes.CATEGORIES,
]);

export const IMAGE_COLLECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.FREE_TEXT,
  OutputTypes.CATEGORIES,
]);

export const AUDIO_COLLECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.FREE_TEXT,
  OutputTypes.CATEGORIES,
]);

export const MOBILE_SURVEY_OUTPUT_TYPES = deepFreeze([
  OutputTypes.FREE_TEXT,
  OutputTypes.CATEGORIES,
  OutputTypes.TEXT_ANNOTATION,
]);

export const DISABLED_DATA_COLLECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.DATE,
  OutputTypes.NUMBER,
  OutputTypes.VIDEO_COLLECTION,
  OutputTypes.IMAGE_COLLECTION,
  OutputTypes.AUDIO_COLLECTION,
  OutputTypes.TIME,
]);

export const DISABLED_IMAGE_SELECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.IMAGE_SELECTION,
  OutputTypes.VIDEO_COLLECTION,
  OutputTypes.IMAGE_COLLECTION,
  OutputTypes.AUDIO_COLLECTION,
]);

export const ASSESSMENT_CATEGORY_OPTIONS = deepFreeze([
  { value: AssessmentCategory.DOMAIN, label: "Domain" },
  { value: AssessmentCategory.PRACTICE, label: "Practice" },
  { value: AssessmentCategory.PROJECT, label: "Project" },
]);

export const PROJECT_TYPE_LABELS = deepFreeze({
  [ProjectType.CUSTOM]: "Start from Scratch",
  [ProjectType.IMAGE_ANNOTATION]: "Image Annotation",
  [ProjectType.AUDIO_TRANSCRIPTION]: "Audio Transcription",
  [ProjectType.VIDEO_TRANSCRIPTION]: "Video Transcription",
  [ProjectType.SEMANTIC_SEGMENTATION]: "Semantic Segmentation",
  [ProjectType.SENTIMENT_ANALYSIS]: "Sentiment Analysis",
  [ProjectType.SEARCH_RELEVANCE]: "Search Relevance",
  [ProjectType.AUDIO_RECORDING]: "Audio Recording",
  [ProjectType.IMAGE_COLLECTION]: "Image Collection",
  [ProjectType.NAMED_ENTITY_RECOGNITION]: "Named Entity Recognition",
  [ProjectType.PARTS_OF_SPEECH_TAGGING]: "Parts of Speech Tagging",
  [ProjectType.PHONETIC_TRANSCRIPTION]: "Phonetic Transcription",
  [ProjectType.INTENT_VARIATION]: "Intent Variation",
  [ProjectType.IMAGE_CATEGORIZATION]: "Image Categorization",
  [ProjectType.TEXT_CATEGORIZATION]: "Text Categorization",
  [ProjectType.AUDIO_CATEGORIZATION]: "Audio Categorization",
  [ProjectType.VIDEO_CATEGORIZATION]: "Video Categorization",
  [ProjectType.LANGUAGE_TEST]: "Language Test",
  [ProjectType.READING_COMPREHENSION_TEST]: "Reading Comprehension Test",
  [ProjectType.LISTENING_COMPREHENSION_TEST]: "Listening Comprehension Test",
  [ProjectType.GRAMMAR_TEST]: "Grammar Test",
  [ProjectType.CONTENT_CREATION_TEST]: "Content Creation Test",
  [ProjectType.SURVEY]: "Survey",
  [ProjectType.AGREEMENT]: "Agreement",
  [ProjectType.CAPTION_TO_IMAGE_MATCHING]: "Caption to Image Matching",
  [ProjectType.VIDEO_COLLECTION]: "Video Collection",
  [ProjectType.MOBILE_SURVEY]: "Mobile Survey",
});

export const PROJECT_TYPE_DETAIL_LABELS = deepFreeze({
  ...PROJECT_TYPE_LABELS,
  [ProjectType.CUSTOM]: "Custom",
});

const _projectTypeOptions = [
  { value: ProjectType.CUSTOM, label: PROJECT_TYPE_LABELS[ProjectType.CUSTOM] },
  {
    label: "Image",
    options: [
      {
        value: ProjectType.IMAGE_ANNOTATION,
        label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_ANNOTATION],
      },
      {
        value: ProjectType.IMAGE_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_CATEGORIZATION],
      },
      {
        value: ProjectType.SEMANTIC_SEGMENTATION,
        label: PROJECT_TYPE_LABELS[ProjectType.SEMANTIC_SEGMENTATION],
      },
    ],
  },
  {
    label: "Audio",
    options: [
      {
        value: ProjectType.AUDIO_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_TRANSCRIPTION],
      },
      {
        value: ProjectType.AUDIO_RECORDING,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_RECORDING],
      },
      {
        value: ProjectType.AUDIO_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_CATEGORIZATION],
      },
    ],
  },
  {
    label: "Video",
    options: [
      {
        value: ProjectType.VIDEO_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.VIDEO_TRANSCRIPTION],
      },
      {
        value: ProjectType.VIDEO_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.VIDEO_CATEGORIZATION],
      },
      {
        value: ProjectType.VIDEO_COLLECTION,
        label: PROJECT_TYPE_LABELS[ProjectType.VIDEO_COLLECTION],
      },
    ],
  },
  {
    label: "Text",
    options: [
      {
        value: ProjectType.SENTIMENT_ANALYSIS,
        label: PROJECT_TYPE_LABELS[ProjectType.SENTIMENT_ANALYSIS],
      },
      {
        value: ProjectType.SEARCH_RELEVANCE,
        label: PROJECT_TYPE_LABELS[ProjectType.SEARCH_RELEVANCE],
      },
      {
        value: ProjectType.NAMED_ENTITY_RECOGNITION,
        label: PROJECT_TYPE_LABELS[ProjectType.NAMED_ENTITY_RECOGNITION],
      },
      {
        value: ProjectType.PARTS_OF_SPEECH_TAGGING,
        label: PROJECT_TYPE_LABELS[ProjectType.PARTS_OF_SPEECH_TAGGING],
      },
      {
        value: ProjectType.PHONETIC_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.PHONETIC_TRANSCRIPTION],
      },
      {
        value: ProjectType.INTENT_VARIATION,
        label: PROJECT_TYPE_LABELS[ProjectType.INTENT_VARIATION],
      },
      {
        value: ProjectType.TEXT_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.TEXT_CATEGORIZATION],
      },
      {
        value: ProjectType.MOBILE_SURVEY,
        label: PROJECT_TYPE_LABELS[ProjectType.MOBILE_SURVEY],
      },
    ],
  },
];

const _projectTypeOptionsWithImageCollection = [..._projectTypeOptions];
_projectTypeOptionsWithImageCollection[1] = {
  label: "Image",
  options: [
    {
      value: ProjectType.IMAGE_ANNOTATION,
      label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_ANNOTATION],
    },
    {
      value: ProjectType.IMAGE_CATEGORIZATION,
      label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_CATEGORIZATION],
    },
    {
      value: ProjectType.IMAGE_COLLECTION,
      label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_COLLECTION],
    },
    {
      value: ProjectType.SEMANTIC_SEGMENTATION,
      label: PROJECT_TYPE_LABELS[ProjectType.SEMANTIC_SEGMENTATION],
    },
  ],
};

export const PROJECT_TYPE_OPTIONS = deepFreeze(_projectTypeOptions);
export const PROJECT_TYPE_OPTIONS_WITH_IMAGE_COLLECTION = deepFreeze(
  _projectTypeOptionsWithImageCollection
);

export const DEPRECATED_PROJECT_TYPES = deepFreeze([
  ProjectType.CUSTOM,
  ProjectType.IMAGE_ANNOTATION,
  ProjectType.IMAGE_CATEGORIZATION,
  ProjectType.SEMANTIC_SEGMENTATION,
  ProjectType.CAPTION_TO_IMAGE_MATCHING,
  ProjectType.VIDEO_TRANSCRIPTION,
  ProjectType.VIDEO_CATEGORIZATION,
  ProjectType.VIDEO_COLLECTION,
  ProjectType.SENTIMENT_ANALYSIS,
  ProjectType.SEARCH_RELEVANCE,
  ProjectType.PARTS_OF_SPEECH_TAGGING,
  ProjectType.PHONETIC_TRANSCRIPTION,
  ProjectType.INTENT_VARIATION,
  ProjectType.TEXT_CATEGORIZATION,
]);

const _generalAssessmentTypeOptions = [
  {
    label: "General Tests",
    options: [
      {
        value: ProjectType.LANGUAGE_TEST,
        label: PROJECT_TYPE_LABELS[ProjectType.LANGUAGE_TEST],
      },
      {
        value: ProjectType.READING_COMPREHENSION_TEST,
        label: PROJECT_TYPE_LABELS[ProjectType.READING_COMPREHENSION_TEST],
      },
      {
        value: ProjectType.LISTENING_COMPREHENSION_TEST,
        label: PROJECT_TYPE_LABELS[ProjectType.LISTENING_COMPREHENSION_TEST],
      },
      {
        value: ProjectType.GRAMMAR_TEST,
        label: PROJECT_TYPE_LABELS[ProjectType.GRAMMAR_TEST],
      },
      {
        value: ProjectType.CONTENT_CREATION_TEST,
        label: PROJECT_TYPE_LABELS[ProjectType.CONTENT_CREATION_TEST],
      },
    ],
  },
  {
    label: "Other",
    options: [
      {
        value: ProjectType.SURVEY,
        label: PROJECT_TYPE_LABELS[ProjectType.SURVEY],
      },
      {
        value: ProjectType.AGREEMENT,
        label: PROJECT_TYPE_LABELS[ProjectType.AGREEMENT],
      },
    ],
  },
];

export const ASSESSMENT_TYPE_OPTIONS = deepFreeze([
  { value: ProjectType.CUSTOM, label: PROJECT_TYPE_LABELS[ProjectType.CUSTOM] },
  {
    label: "Image",
    options: [
      {
        value: ProjectType.IMAGE_ANNOTATION,
        label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_ANNOTATION],
      },
      {
        value: ProjectType.IMAGE_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.IMAGE_CATEGORIZATION],
      },
      {
        value: ProjectType.SEMANTIC_SEGMENTATION,
        label: PROJECT_TYPE_LABELS[ProjectType.SEMANTIC_SEGMENTATION],
      },
    ],
  },
  {
    label: "Audio",
    options: [
      {
        value: ProjectType.AUDIO_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_TRANSCRIPTION],
      },
      {
        value: ProjectType.AUDIO_RECORDING,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_RECORDING],
      },
      {
        value: ProjectType.AUDIO_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.AUDIO_CATEGORIZATION],
      },
    ],
  },
  {
    label: "Video",
    options: [
      {
        value: ProjectType.VIDEO_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.VIDEO_TRANSCRIPTION],
      },
      {
        value: ProjectType.VIDEO_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.VIDEO_CATEGORIZATION],
      },
    ],
  },
  {
    label: "Text",
    options: [
      {
        value: ProjectType.SENTIMENT_ANALYSIS,
        label: PROJECT_TYPE_LABELS[ProjectType.SENTIMENT_ANALYSIS],
      },
      {
        value: ProjectType.SEARCH_RELEVANCE,
        label: PROJECT_TYPE_LABELS[ProjectType.SEARCH_RELEVANCE],
      },
      {
        value: ProjectType.NAMED_ENTITY_RECOGNITION,
        label: PROJECT_TYPE_LABELS[ProjectType.NAMED_ENTITY_RECOGNITION],
      },
      {
        value: ProjectType.PARTS_OF_SPEECH_TAGGING,
        label: PROJECT_TYPE_LABELS[ProjectType.PARTS_OF_SPEECH_TAGGING],
      },
      {
        value: ProjectType.PHONETIC_TRANSCRIPTION,
        label: PROJECT_TYPE_LABELS[ProjectType.PHONETIC_TRANSCRIPTION],
      },
      {
        value: ProjectType.INTENT_VARIATION,
        label: PROJECT_TYPE_LABELS[ProjectType.INTENT_VARIATION],
      },
      {
        value: ProjectType.TEXT_CATEGORIZATION,
        label: PROJECT_TYPE_LABELS[ProjectType.TEXT_CATEGORIZATION],
      },
    ],
  },
  ..._generalAssessmentTypeOptions,
]);

export const EDITABLE_PROJECT_TYPES = deepFreeze([
  ProjectType.AUDIO_TRANSCRIPTION,
  ProjectType.AUDIO_CATEGORIZATION,
  ProjectType.IMAGE_CATEGORIZATION,
  ProjectType.CAPTION_TO_IMAGE_MATCHING,
  ProjectType.VIDEO_TRANSCRIPTION,
  ProjectType.VIDEO_CATEGORIZATION,
  ProjectType.SENTIMENT_ANALYSIS,
  ProjectType.SEARCH_RELEVANCE,
  ProjectType.PHONETIC_TRANSCRIPTION,
  ProjectType.INTENT_VARIATION,
  ProjectType.TEXT_CATEGORIZATION,
  ProjectType.LANGUAGE_TEST,
  ProjectType.READING_COMPREHENSION_TEST,
  ProjectType.GRAMMAR_TEST,
  ProjectType.CONTENT_CREATION_TEST,
  ProjectType.SURVEY,
  ProjectType.AGREEMENT,
  ProjectType.MOBILE_SURVEY,
]);

export const DATA_COLLECTION_OUTPUT_TYPES = deepFreeze([
  OutputTypes.AUDIO_COLLECTION,
  OutputTypes.IMAGE_COLLECTION,
  OutputTypes.VIDEO_COLLECTION,
]);

// TODO: update when there are new labels or new language model
export const NAMED_ENTITY_LABELS = deepFreeze([
  // these are labels from https://spacy.io/models/en#en_core_web_trf
  "CARDINAL",
  "DATE",
  "EVENT",
  "FAC",
  "GPE",
  "LANGUAGE",
  "LAW",
  "LOC",
  "MONEY",
  "NORP",
  "ORDINAL",
  "ORG",
  "PERCENT",
  "PERSON",
  "PRODUCT",
  "QUANTITY",
  "TIME",
  "WORK_OF_ART",
  // this is a label from https://spacy.io/models/xx
  "MISC",
]);

// TODO: update when there are new tags or new language model
export const PARTS_OF_SPEECH_TAGS = deepFreeze([
  // these are labels from https://spacy.io/models/en#en_core_web_trf
  "$",
  "''",
  ",",
  "-LRB-",
  "-RRB-",
  ".",
  ":",
  "ADD",
  "AFX",
  "CC",
  "CD",
  "DT",
  "EX",
  "FW",
  "HYPH",
  "IN",
  "JJ",
  "JJR",
  "JJS",
  "LS",
  "MD",
  "NFP",
  "NN",
  "NNP",
  "NNPS",
  "NNS",
  "PDT",
  "POS",
  "PRP",
  "PRP$",
  "RB",
  "RBR",
  "RBS",
  "RP",
  "SYM",
  "TO",
  "UH",
  "VB",
  "VBD",
  "VBG",
  "VBN",
  "VBP",
  "VBZ",
  "WDT",
  "WP",
  "WP$",
  "WRB",
  "XX",
  "``",
]);

export const NO_VALUE_PLACEHOLDER = "None";
export const CLONE_SUFFIX = "- Copy";
export const REWARD_PER_UNIT_MIN = "0.00";
export const MIN_TIME_SECONDS = 1;
export const MAX_TIME_SECONDS = 60 * 60 * 169 - 1;
export const ESTIMATED_TIME_DEFAULT_IN_SECONDS = 60 * 5;
export const NEWLINE_SEPARATOR = "\n";
export const NEWLINE_CHARACTER = `
`;

export const AUTOMATED_REVISION_BATCH_DEFAULT_NAME = "[automated] Revision batch";
export const PROJECT_USER_ROLE_CONTRIBUTOR = "Contributor";
export const PROJECT_USER_ROLE_QA_CONTRIBUTOR = "QA Contributor";

export const MEMBERSHIP_TYPE_ORG = "organization";

export const TOKEN_LOCATION_TYPE_LOCAL_STORAGE = "localStorage";

export const AI_WORKBENCH_DOMAIN = getEnv("AI_WORKBENCH_DOMAIN");
export const APAPI_URL = getEnv("APAPI_URL");
export const CAT_API_URL = getEnv("CAT_API_URL");
export const ROOT_PATH = getEnv("ROOT_PATH");
export const SNAKE_URL = getEnv("SNAKE_URL");
export const WALRUS_URL = getEnv("WALRUS_URL");
export const TELUS_INT_ORG_ID = getEnv("TELUS_INT_ORG_ID");

export const LIONBRIDGE_AI_URL = "https://www.telusinternational.com/solutions/ai-data-solutions";
export const ACCOUNT_MANAGER_EMAIL = "andrew.lavine@lionbridge.com";
export const SUPPORT_EMAIL = "TI_AICommunitySupport@telusinternational.com";
export const TELUS_INTERNATIONAL_CONTACT_URL = "https://www.telusinternational.com/contact";
export const GOOGLE_PLAYSTORE_URL =
  "https://play.google.com/store/apps/details?id=com.telus.ai.collection";
export const APPSTORE_URL = "https://apps.apple.com/us/app/telus-international-ai/id1592601749";

export const PUBLIC_SIGNUP_AGREEMENT = "sa";
export const PUBLIC_SIGNUP_AGREEMENT_URL =
  "https://lionbridge.ai/wp-content/assets/Lionbridge_AI_Platform_Services_Agreement_June_2020.pdf";
export const TERMS_AND_CONDITIONS_URL =
  "https://lionbridge.ai/wp-content/assets/Lionbridge-AI-Platform-Access-Acknowledgment-June-2020.pdf";

export const SYSTEM_MAINTENANCE_PATH = `${SNAKE_URL}/system_maintenance`;

export const LIONBRIDGE_ORG_ID = getEnv("LIONBRIDGE_ORG_ID");
export const LIONBRIDGE_CMP_URL = getEnv("LIONBRIDGE_CMP_URL") ?? "";

export const CHART_ITEM_QUOTA = 5;

export const ALL_CONTRIBUTORS = "ALL";

export const DEFAULT_LANGUAGE_CODE = "en-GB";
export const DEFAULT_LANGUAGE = "English (United Kingdom)";
export const TRANSCRIBABLE_LANGUAGES = deepFreeze(["en", "ja", "es", "pt", "fr", "it", "de"]);

export const MIN_SCORE_THRESHOLD = 0;
export const MAX_SCORE_THRESHOLD = 1;

export const USER_LIST_PAGE_LIMIT = 100;
export const PROJECT_TITLE_FILTER_MAX_LENGTH = 40;

export const ELIGIBILITY_CONDITION_LABELS = {
  [EligibilityConditions.PRIMARY_LANGUAGE]: "Primary Language",
  [EligibilityConditions.SECONDARY_LANGUAGE]: "Secondary Language",
  [EligibilityConditions.LOCATION]: "Location",
};

export const ELIGIBILITY_CONDITION_OPTIONS = [
  {
    label: "Condition Type",
    value: "",
  },
  {
    label: "Primary Language",
    value: EligibilityConditions.PRIMARY_LANGUAGE,
  },
  {
    label: "Secondary Language",
    value: EligibilityConditions.SECONDARY_LANGUAGE,
  },
  {
    label: "Location",
    value: EligibilityConditions.LOCATION,
  },
];

export const LANGUAGE_OPTIONS = deepFreeze(
  LANGUAGES.map((language) => ({
    value: language["IETFTag"],
    label: language["DisplayName"],
  }))
);

export const ASSESSMENT_CATEGORY_TYPE_OPTIONS = [
  {
    label: "Single Category",
    value: AssessmentCategoryTypes.SINGLE_CATEGORY,
  },
  {
    label: "Multiple Categories",
    value: AssessmentCategoryTypes.MULTIPLE_CATEGORY,
  },
];

export const HEADER_HEIGHT = (): string => {
  const isGlobalNavigationEnabled = useFeatureFlag("KANRI_ENABLE_GLOBAL_NAVIGATION");
  if (isGlobalNavigationEnabled) {
    return "0";
  }
  return "3.5625rem";
};
export const ACCOUNT_EXPIRING_ALERT_HEIGHT = (): string => {
  const isGlobalNavigationEnabled = useFeatureFlag("KANRI_ENABLE_GLOBAL_NAVIGATION");
  if (isGlobalNavigationEnabled) {
    return "0.0625rem";
  }
  return "3.625rem";
};
export const TI_SPECIFIC_ROLES = [UserRole.TESTING_MANAGER, UserRole.PROJECT_MANAGEMENT_OFFICER];
export const SPLIT_IO_AUTH_KEY = getEnv("SPLIT_IO_AUTH_KEY") || "";

export const CURRENT_YEAR = new Date().getFullYear().toString();
export const COMPANY_NAME = "TELUS Digital";
